// @TODO: split this into small files by feature
import { lazy } from 'react';
import {
    RouteAccess,
    type RouteConfigExtended,
    TabValue,
    accountClosedPath,
    bookingPreviewPath,
    changeLearningLanguageSuccessPath,
    changeSubscriptionFailedPath,
    optionalArg,
    productOrderPath,
    productPurchasePath,
    refusedPaymentPath,
    studentAchievementsPath,
    studentBookingPath,
    studentChangedPaymentMethodPath,
    studentChapterFlashcardsPath,
    studentClassDetailsPath,
    studentClassesPath,
    studentCourseOverviewPath,
    studentCreditsAddedPath,
    studentGrammarItemPath,
    studentGrammarPath,
    studentHomePath,
    studentHomeworkPath,
    studentLemonadePath,
    studentLessonDetailsPath,
    studentLessonFlashcardsPath,
    studentLingobotPath,
    studentPreClassPath,
    studentProfilePath,
    studentQuizPath,
    studentReactivateCoursePath,
    studentReactivatedPath,
    studentReferralsPath,
    studentSettingsPath,
    studentStorePath,
    studentTeacherPath,
    studentVocabularyPath,
} from '@lingoda/router';
import { quizRoutes } from '@lingoda/quiz';
import { isNewCoursePageEnabled } from '@lingoda/monolith-feature-flags';
import { challengesRoutes } from '@lingoda/student-challenges';
import { practiceSequenceRoutes } from '@lingoda/student-practice-sequence';
import { grammarTrainerRoutes } from '../grammar-trainer/routes';
import { practiceDashboardRoutes } from '../practice/dashboard/routes';
import { CourseOverviewPage } from '../course/pages/CourseOverviewPage/lazy';
import { CourseOverviewLegacyPage } from '../course/pages/CourseOverviewLegacyPage/lazy';
import { LingobotPageProtected as LingobotPage } from '../practice/pages/Lingobot/ProtectedPage';

const MyClasses = lazy(() => import('../entrypoint/pages/MyClasses'));
const Booking = lazy(() => import('../booking/pages/Booking'));
const BookingPreview = lazy(() => import('../preview/pages/BookingPreview'));
const ClassDetails = lazy(() => import('../classes/pages/ClassDetails'));
const LessonDetails = lazy(() => import('../classes/pages/LessonDetails'));
const Settings = lazy(() => import('../account-settings/pages/Root'));
const StudentProfile = lazy(() => import('../profile/pages/StudentProfile/StudentProfile'));
const PreClassChecklist = lazy(() => import('../classes/pages/PreClassChecklist'));
const Teacher = lazy(() => import('../teachers/pages/TeacherProfile'));
const AccountClosed = lazy(() => import('../components/AccountClosed'));
const RefusedPayment = lazy(() => import('../components/RefusedPayment'));
const ReactivateCourse = lazy(() => import('../billing/pages/ReactivateCourse'));
const Reactivated = lazy(() => import('../store/pages/Reactivated'));
const CreditsAdded = lazy(() => import('../store/pages/CreditsAdded'));
const ChangedPaymentMethod = lazy(() => import('../store/pages/ChangedPaymentMethod'));
const Store = lazy(() => import('../store/pages/Store'));
const ChangeSubscriptionFailed = lazy(() => import('../store/pages/ChangeSubscriptionFailed'));
const ProductPurchase = lazy(() => import('../store/pages/ProductPurchase'));
const H5PExercise = lazy(() => import('../practice/pages/H5PExercise'));
const ChapterFlashcards = lazy(
    () => import('../practice/flashcards/chapter/ChapterFlashcardsPage'),
);
const LessonFlashcards = lazy(() => import('../practice/flashcards/lesson/LessonFlashcardsPage'));
const LessonVocabulary = lazy(() => import('../practice/pages/LessonVocabulary/LessonVocabulary'));
const LessonGrammarList = lazy(() => import('../practice/pages/LessonGrammar/LessonGrammarList'));
const LessonGrammarItem = lazy(() => import('../practice/pages/LessonGrammar/LessonGrammarItem'));
const Today = lazy(() => import('../entrypoint/pages/Today'));
const MyAchievements = lazy(() => import('../milestones/pages/MyAchievements'));
const ChangeLearningLanguageSuccess = lazy(
    () => import('../store/pages/ChangeLearningLanguageSuccess'),
);
const LemonadePage = lazy(() => import('../practice/lemonade/route'));
const Referrals = lazy(() => import('../referrals/pages/Referrals'));

const student: RouteConfigExtended[] = [
    {
        exact: true,
        component: ProductPurchase,
        path: productOrderPath.raw,
        config: {
            access: RouteAccess.Public,
            hideFooter: true,
            hideNavigation: true,
            tab: TabValue.Store,
        },
        label: 'Purchase',
    },
    {
        exact: true,
        component: ProductPurchase,
        path: productPurchasePath.raw,
        config: {
            hideFooter: true,
            access: RouteAccess.Public,
            hideNavigation: true,
            tab: TabValue.Store,
        },
        label: 'Purchase',
    },
    {
        exact: true,
        component: Today,
        path: studentHomePath.raw,
        label: 'Home',
        config: {
            tab: TabValue.Home,
        },
    },
    {
        exact: true,
        component: MyAchievements,
        path: studentAchievementsPath.raw,
        label: 'MyAchievements',
        config: {
            tab: TabValue.Home,
        },
    },
    {
        exact: true,
        component: MyClasses,
        path: studentClassesPath.raw,
        label: 'MyClasses',
        config: {
            tab: TabValue.MyClasses,
            hideFooter: true,
            pageNotScrollable: true,
        },
    },
    {
        exact: true,
        component: Booking,
        path: studentBookingPath.raw,
        label: 'Booking',
        config: {
            tab: TabValue.BookClasses,
            hideFooter: true,
            pageNotScrollable: true,
        },
    },
    {
        exact: true,
        component: BookingPreview,
        path: bookingPreviewPath.raw,
        label: 'BookingPreview',
        config: {
            headerIsPortal: true,
            hideFooter: true,
            pageNotScrollable: true,
            tab: TabValue.BookClasses,
        },
    },
    {
        exact: true,
        component: ClassDetails,
        path: studentClassDetailsPath.raw,
        label: 'ClassDetails',
        config: {
            tab: TabValue.MyClasses,
            hideMobileBottomNavbar: true,
            hideHeader: 'phablet',
        },
    },
    {
        exact: true,
        component: LessonDetails,
        path: studentLessonDetailsPath.raw,
        label: 'LessonDetails',
        config: {
            tab: TabValue.CourseOverview,
            hideMobileBottomNavbar: true,
            hideHeader: 'phablet',
        },
    },
    {
        exact: true,
        component: StudentProfile,
        path: studentProfilePath.raw,
        label: 'Student Profile',
        config: {
            tab: TabValue.Settings,
        },
    },
    {
        exact: true,
        component: Settings,
        path: optionalArg(studentSettingsPath.raw, 'page'),
        label: 'Profile', // TODO: change label to 'Settings' when StudentProfile page released
        config: {
            tab: TabValue.Settings,
        },
    },
    {
        exact: true,
        component: Referrals,
        path: studentReferralsPath.raw,
        label: 'Referral',
        config: {
            tab: TabValue.Settings,
        },
    },
    {
        exact: true,
        render: () => {
            if (isNewCoursePageEnabled()) {
                return <CourseOverviewPage />;
            }

            return <CourseOverviewLegacyPage />;
        },
        path: optionalArg(studentCourseOverviewPath.raw, 'moduleName'),
        label: 'CourseOverview',
        config: {
            tab: TabValue.CourseOverview,
            hideFooter: () => isNewCoursePageEnabled(),
            pageNotScrollable: () => isNewCoursePageEnabled(),
        },
    },
    {
        exact: true,
        component: LessonVocabulary,
        path: studentVocabularyPath.raw,
        label: 'LessonVocabulary',
        config: {
            tab: TabValue.CourseOverview,
            hideMobileBottomNavbar: true,
        },
    },
    {
        exact: true,
        component: LessonGrammarList,
        path: studentGrammarPath.raw,
        label: 'LessonGrammarList',
        config: {
            tab: TabValue.CourseOverview,
            hideMobileBottomNavbar: true,
        },
    },
    {
        exact: true,
        component: LessonGrammarItem,
        path: studentGrammarItemPath.raw,
        label: 'LessonGrammarItem',
        config: {
            tab: TabValue.CourseOverview,
            hideMobileBottomNavbar: true,
        },
    },
    {
        exact: true,
        component: PreClassChecklist,
        path: studentPreClassPath.raw,
        label: 'PreClassChecklist',
        config: {
            tab: TabValue.MyClasses,
        },
    },
    {
        exact: true,
        component: Teacher,
        path: studentTeacherPath.raw,
        label: 'TeacherDetails',
        config: {
            tab: TabValue.MyClasses,
        },
    },
    {
        exact: true,
        component: AccountClosed,
        path: accountClosedPath.raw,
        label: 'AccountClosed',
        config: undefined,
    },
    {
        exact: true,
        component: RefusedPayment,
        path: refusedPaymentPath.raw,
        label: 'RefusedPayment',
        config: {
            tab: TabValue.Store,
        },
    },
    {
        exact: true,
        component: ReactivateCourse,
        path: studentReactivateCoursePath.raw,
        label: 'ReactivateCourse',
        config: {
            tab: TabValue.Store,
        },
    },
    {
        exact: true,
        component: Reactivated,
        path: studentReactivatedPath.raw,
        label: 'Reactivated',
        config: undefined,
    },
    {
        exact: true,
        component: CreditsAdded,
        path: studentCreditsAddedPath.raw,
        label: 'CreditsAdded',
        config: {
            tab: TabValue.Store,
        },
    },
    {
        exact: true,
        component: ChangedPaymentMethod,
        path: studentChangedPaymentMethodPath.raw,
        label: 'ChangedPaymentMethod',
        config: undefined,
    },
    {
        exact: true,
        component: Store,
        path: studentStorePath.raw,
        label: 'Store',
        config: {
            tab: TabValue.Store,
        },
    },
    {
        exact: true,
        component: ChangeSubscriptionFailed,
        path: changeSubscriptionFailedPath.raw,
        label: 'ChangeSubscriptionFailed',
        config: undefined,
    },
    ...practiceDashboardRoutes,
    {
        exact: true,
        component: H5PExercise,
        path: studentQuizPath.raw,
        label: 'Quiz',
        config: {
            hideHeader: true,
            hideFooter: true,
            hideMobileBottomNavbar: true,
            tab: TabValue.Practice,
        },
    },
    {
        exact: true,
        component: H5PExercise,
        path: studentHomeworkPath.raw,
        label: 'Homework',
        config: {
            hideHeader: true,
            hideFooter: true,
            hideDixa: true,
            tab: TabValue.Practice,
            hideMobileBottomNavbar: true,
        },
    },
    {
        exact: true,
        component: ChangeLearningLanguageSuccess,
        path: changeLearningLanguageSuccessPath.raw,
        label: 'ChangedLearningLanguage',
        config: undefined,
    },
    {
        exact: true,
        component: LessonFlashcards,
        path: studentLessonFlashcardsPath.raw,
        label: 'LessonFlashcards',
        config: {
            hideHeader: true,
            hideFooter: true,
            hideMobileBottomNavbar: true,
            hideDixa: true,
            tab: TabValue.CourseOverview,
            hideNotifications: true,
        },
    },
    {
        exact: true,
        component: ChapterFlashcards,
        path: studentChapterFlashcardsPath.raw,
        label: 'ChapterFlashcards',
        config: {
            hideHeader: true,
            hideFooter: true,
            hideMobileBottomNavbar: true,
            hideDixa: true,
            tab: TabValue.CourseOverview,
            hideNotifications: true,
        },
    },
    {
        exact: true,
        component: LemonadePage,
        path: studentLemonadePath.raw,
        label: 'Lemonade',
        config: {
            hideHeader: true,
            hideFooter: true,
            hideDixa: true,
            hideMobileBottomNavbar: true,
            tab: TabValue.Practice,
            hideNotifications: true,
        },
    },
    {
        exact: true,
        component: LingobotPage,
        path: studentLingobotPath.raw,
        label: 'Lingobot',
        config: {
            hideHeader: true,
            hideFooter: true,
            hideDixa: true,
            hideMobileBottomNavbar: true,
            tab: TabValue.Practice,
            hideNotifications: true,
        },
    },
    ...quizRoutes,
    ...practiceSequenceRoutes,
    ...challengesRoutes,
    ...grammarTrainerRoutes,
];

export default student;
